import React, { FC, FormEvent, ReactElement, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { addDays, isAfter, isBefore, startOfDay } from 'date-fns'

import { Button, InfoListBlock, InfoListItem, Input, LoadingSpinner, RadioButtonsList } from '../../../components'
import { DateInput, TimeInput } from '../../../compositions'
import { ConnectedSpecialDaysList } from '../../../connectors'
import { LeaveOfAbsenceForm } from '../../../entities/LeaveOfAbsences/LeaveOfAbsences'
import { formatDate, getDateTimeFromForm } from '../../../helpers/date'
import trans from '../../../helpers/trans'
import { useFormState } from '../../../hooks/useFormState'
import { DateFormat } from '../../../types/dateFormatTypes'

import './AddLeaveOfAbsenceForm.scss'

interface AddLeaveOfAbsenceFormProps {
    isLoading?: boolean
    startDate: Date
    endDate: Date
    onSubmit: (formData: LeaveOfAbsenceForm) => void
    className?: string
}

const AddLeaveOfAbsenceForm: FC<AddLeaveOfAbsenceFormProps> = ({
    isLoading = false,
    startDate: formStartDate,
    endDate: formEndDate,
    onSubmit,
    className = '',
}): ReactElement => {
    const history = useHistory()
    const minDate = startOfDay(addDays(new Date(), 1))
    const minDateString = formatDate(minDate, DateFormat.inputDate)

    const clampToMinDate = (date: Date) => (isAfter(date, minDate) ? date : minDate)

    const [form, updateFormField] = useFormState({
        startDate: clampToMinDate(formStartDate),
        endDate: clampToMinDate(formEndDate),
        startTime: '09:00',
        endTime: '18:00',
        reason: '',
        multipleDays: false,
    })

    const startDateTime = useMemo(() => getDateTimeFromForm(form.startDate, form.startTime, form.multipleDays, true), [
        form.startDate,
        form.startTime,
        form.multipleDays,
    ])

    const endDateTime = useMemo(
        () => getDateTimeFromForm(form.startDate, form.endTime, form.multipleDays, false, form.endDate),
        [form.startDate, form.endDate, form.endTime, form.multipleDays],
    )

    const isDateRangeValid =
        startDateTime !== null &&
        endDateTime !== null &&
        isAfter(startDateTime, minDate) &&
        isBefore(startDateTime, endDateTime)

    const canSubmit = isDateRangeValid && form.reason.trim().length > 0

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (! startDateTime || ! endDateTime) return

        onSubmit({
            startDate: startDateTime,
            endDate: endDateTime,
            reason: form.reason,
        })

        history.push(`/${trans('nav.absences.link')}/${trans('nav.leaveOfAbsenceRequests.link')}`)
    }

    if (isLoading) return <LoadingSpinner />

    return (
        <form onSubmit={handleFormSubmit} className={`add-leave-of-absence-form ${className}`}>
            <RadioButtonsList
                className="add-leave-of-absence-form__multiple-days-radio-list"
                checked={form.multipleDays ? 1 : 0}
                buttons={[
                    { label: trans('containers.leaveOfAbsenceDetailContainer.singleDay') },
                    { label: trans('containers.leaveOfAbsenceDetailContainer.multipleDays') },
                ]}
                onRadioChange={() => updateFormField('multipleDays', ! form.multipleDays)}
            />

            <ul className="add-leave-of-absence-form__list">
                {! form.multipleDays && (
                    <>
                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceSingleDate')}
                            className="add-leave-of-absence-form__list-item"
                        >
                            <DateInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceSingleDate')}
                                type="date"
                                min={minDateString}
                                value={form.startDate}
                                onChange={(date) => updateFormField('startDate', date)}
                            />
                        </InfoListItem>

                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartTime')}
                            className="add-leave-of-absence-form__list-item"
                        >
                            <TimeInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartTime')}
                                value={form.startTime}
                                onChange={(time) => updateFormField('startTime', time)}
                                className="add-leave-of-absence-form__time-input"
                            />
                        </InfoListItem>

                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndTime')}
                            className="add-leave-of-absence-form__list-item"
                        >
                            <TimeInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndTime')}
                                value={form.endTime}
                                onChange={(time) => updateFormField('endTime', time)}
                                className="add-leave-of-absence-form__time-input"
                            />
                        </InfoListItem>
                    </>
                )}

                {form.multipleDays && (
                    <>
                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartDate')}
                            className="add-leave-of-absence-form__list-item"
                        >
                            <DateInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartDate')}
                                type="date"
                                min={minDateString}
                                value={form.startDate}
                                onChange={(date) => updateFormField('startDate', date)}
                            />
                        </InfoListItem>

                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndDate')}
                            className="add-leave-of-absence-form__list-item"
                        >
                            <DateInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndDate')}
                                type="date"
                                min={minDateString}
                                value={form.endDate}
                                onChange={(date) => updateFormField('endDate', date)}
                            />
                        </InfoListItem>
                    </>
                )}

                {startDateTime && endDateTime && (
                    <ConnectedSpecialDaysList startDate={startDateTime} endDate={endDateTime} />
                )}

                <InfoListBlock
                    label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceReason')}
                    className="add-leave-of-absence-form__list-block"
                >
                    <Input
                        onChange={(e) => updateFormField('reason', e.target.value)}
                        value={form.reason}
                        placeholder={trans('common.descriptionOfThisRequest')}
                        className="add-leave-of-absence-form__textarea-input"
                    />
                </InfoListBlock>
            </ul>

            <Button
                disabled={! canSubmit}
                type="submit"
                className="add-leave-of-absence-form__submit-button"
                text={trans('common.request')}
            />
        </form>
    )
}

export default AddLeaveOfAbsenceForm
